import {
  Box,
  Flex,
  HStack,
  Stack,
  VStack,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiCheck, FiX, FiMail, FiCopy, FiGift } from 'react-icons/fi';
import { GoPeople } from 'react-icons/go';
import { IoTrendingUp, IoTime, IoFlash } from 'react-icons/io5';
import { FaRegFolder } from 'react-icons/fa6';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanButton } from '../helivan/HelivanButton';

const ContactModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('info@helivan.io');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="12px">
        <ModalHeader pb={2}>
          <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
            Contact Us
          </HelivanText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="center">
            <Flex
              bg={HelivanColor.text.blueLight}
              p={3}
              borderRadius="full"
            >
              <FiMail size={24} color={HelivanColor.text.helivanBlue} />
            </Flex>
            <VStack spacing={2}>
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                Get in touch with us at
              </HelivanText>
              <Flex
                bg={HelivanColor.background.secondary}
                px={4}
                py={2}
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                onClick={handleCopy}
                _hover={{ bg: HelivanColor.background.lightestGrey }}
                align="center"
                gap={2}
              >
                <HelivanText variant="largeBody" colorOverride={HelivanColor.text.helivanBlue}>
                  info@helivan.io
                </HelivanText>
                <Icon as={FiCopy} color={HelivanColor.text.blueMid} boxSize={4} />
              </Flex>
              <HelivanText variant="smallBody" colorOverride={copied ? 'green.500' : 'transparent'}>
                {copied ? 'Copied!' : '\u00A0'}
              </HelivanText>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: { text: string; included: boolean }[];
  buttonText: string;
  buttonVariant: 'green' | 'outline' | 'ghost';
  isPopular?: boolean;
}

const tiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: '/mo',
    description: 'Get started with basic access',
    features: [
      { text: '1 project', included: true },
      { text: '1 agent', included: true },
      { text: 'Updates every 6 hours', included: true },
      { text: 'Create organizations', included: false },
      { text: 'Team collaboration', included: false },
    ],
    buttonText: 'Current Plan',
    buttonVariant: 'ghost',
  },
  {
    name: 'Research',
    price: '$9',
    period: '/mo',
    description: 'For individual researchers',
    features: [
      { text: '3 projects', included: true },
      { text: 'Up to 10 agents', included: true },
      { text: 'Updates every 6 hours', included: true },
      { text: 'Add your own agents', included: true },
      { text: 'Share projects', included: true },
    ],
    buttonText: 'Upgrade',
    buttonVariant: 'outline',
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/mo',
    description: 'For power users',
    features: [
      { text: '5 projects', included: true },
      { text: 'Up to 50 agents', included: true },
      { text: 'Updates every 6 hours', included: true },
      { text: 'Add your own agents', included: true },
      { text: 'Share projects', included: true },
    ],
    buttonText: 'Upgrade',
    buttonVariant: 'green',
    isPopular: true,
  },
  {
    name: 'Team',
    price: '$129',
    period: '/mo',
    description: 'For teams and collaboration',
    features: [
      { text: '10 projects', included: true },
      { text: 'Up to 100 agents', included: true },
      { text: 'Updates every 6 hours', included: true },
      { text: 'Organization with 5 seats', included: true },
      { text: 'Team collaboration', included: true },
    ],
    buttonText: 'Upgrade',
    buttonVariant: 'outline',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations',
    features: [
      { text: 'Unlimited projects', included: true },
      { text: 'Unlimited agents', included: true },
      { text: 'Custom update frequency', included: true },
      { text: 'Priority support', included: true },
      { text: 'Custom integrations', included: true },
    ],
    buttonText: 'Contact Sales',
    buttonVariant: 'outline',
  },
];

interface ValueProp {
  icon: React.ElementType;
  title: string;
  description: string;
}

const valueProps: ValueProp[] = [
  {
    icon: IoTime,
    title: 'Continuous Monitoring',
    description: 'Daily automated scans track how your AI agents behave over time. Stay informed without lifting a finger.',
  },
  {
    icon: IoTrendingUp,
    title: 'Change Detection',
    description: 'Get notified when agent behavior shifts. Detect drift from model updates, fine-tuning, or changing contexts before it causes problems.',
  },
  {
    icon: GoPeople,
    title: 'Multi-Agent Tracking',
    description: 'Monitor multiple agents in one place. Compare behaviors across your AI fleet and spot inconsistencies.',
  },
];

const PricingCard = ({ tier, onContactClick }: { tier: PricingTier; onContactClick: () => void }) => {
  const handleButtonClick = () => {
    if (tier.buttonText !== 'Current Plan') {
      onContactClick();
    }
  };

  return (
    <Box
      bg={HelivanColor.background.primary}
      borderRadius="12px"
      border="2px solid"
      borderColor={tier.isPopular ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
      p={6}
      width="220px"
      position="relative"
      _hover={{
        borderColor: HelivanColor.text.helivanBlue,
        boxShadow: '0 4px 12px rgba(0, 71, 125, 0.15)',
      }}
      transition="all 0.2s"
    >
      {tier.isPopular && (
        <Box
          position="absolute"
          top="-12px"
          left="50%"
          transform="translateX(-50%)"
          bg={HelivanColor.text.helivanBlue}
          color="white"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="11px"
          fontWeight={600}
        >
          Most Popular
        </Box>
      )}

      <VStack spacing={4} align="stretch">
        <VStack spacing={1} align="center">
          <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
            {tier.name}
          </HelivanText>
          <HStack spacing={0} align="baseline">
            <HelivanText variant="sectionHeader" colorOverride={HelivanColor.text.black}>
              {tier.price}
            </HelivanText>
            {tier.period && (
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                {tier.period}
              </HelivanText>
            )}
          </HStack>
          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
            {tier.description}
          </HelivanText>
        </VStack>

        <Box height="1px" bg={HelivanColor.border.primary} />

        <List spacing={2}>
          {tier.features.map((feature, index) => (
            <ListItem key={index} display="flex" alignItems="flex-start">
              <ListIcon
                as={feature.included ? FiCheck : FiX}
                color={feature.included ? 'green.500' : HelivanColor.text.gray}
                mt="3px"
              />
              <HelivanText
                variant="smallBody"
                colorOverride={feature.included ? HelivanColor.text.black : HelivanColor.text.gray}
              >
                {feature.text}
              </HelivanText>
            </ListItem>
          ))}
        </List>

        <Box pt={2}>
          <HelivanButton
            variant={tier.buttonVariant}
            size="sm"
            width="full"
            isDisabled={tier.buttonText === 'Current Plan'}
            onClick={handleButtonClick}
          >
            {tier.buttonText}
          </HelivanButton>
        </Box>
      </VStack>
    </Box>
  );
};

export const Pricing = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      <ContactModal isOpen={isOpen} onClose={onClose} />
      {/* Header */}
      <Flex
        width="full"
        alignItems="center"
        py="12px"
        px="24px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={4}
        flexShrink={0}
      >
        <HStack spacing={6}>
          <HelivanText variant="sectionHeader">Pricing</HelivanText>
        </HStack>
      </Flex>

      {/* Content */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={6}
        justifyContent="center"
      >
        <VStack spacing={12} width="full" maxWidth="1200px">
          {/* Beta Banner */}
          <Box
            bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            borderRadius="12px"
            p={5}
            width="full"
            boxShadow="0 4px 14px rgba(16, 185, 129, 0.25)"
          >
            <HStack spacing={4} justify="center" align="center" flexWrap="wrap">
              <Flex
                bg="rgba(255,255,255,0.2)"
                p={2}
                borderRadius="full"
              >
                <Icon as={FiGift} boxSize={6} color="white" />
              </Flex>
              <VStack spacing={0} align={{ base: 'center', md: 'start' }}>
                <HelivanText variant="largeBody" colorOverride="white">
                  Welcome to the Beta!
                </HelivanText>
                <HelivanText variant="smallBody" colorOverride="rgba(255,255,255,0.9)">
                  All pricing tiers are waived during our beta period. Enjoy unlimited access to all features!
                </HelivanText>
              </VStack>
            </HStack>
          </Box>

          {/* Hero Value Proposition */}
          <Box
            bg={HelivanColor.text.helivanBlue}
            borderRadius="12px"
            p={8}
            width="full"
          >
            <VStack spacing={6} textAlign="center" maxWidth="900px" mx="auto">
              <HelivanText variant="sectionHeader" colorOverride="white">
                Monitor Your AI Agents. Get Notified When They Change.
              </HelivanText>
              <HelivanText variant="largeBody" colorOverride="rgba(255,255,255,0.9)">
                AI agents can drift over time due to changes in context, changes in the environment,
                or changes in underlying models. Without monitoring, these changes go unnoticed until
                they cause real problems. Helivan tracks your agents and alerts you when their behaviors
                shift—so you can maintain trust, consistency, and reliability with your users.
              </HelivanText>

              {/* Value Props inline */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} width="full" pt={4}>
                {valueProps.map((prop) => (
                  <VStack key={prop.title} spacing={2} p={4} bg="rgba(255,255,255,0.1)" borderRadius="8px">
                    <Icon as={prop.icon} boxSize={6} color="white" />
                    <HelivanText variant="smallBody+" colorOverride="white">
                      {prop.title}
                    </HelivanText>
                    <HelivanText variant="smallBody" colorOverride="rgba(255,255,255,0.8)">
                      {prop.description}
                    </HelivanText>
                  </VStack>
                ))}
              </SimpleGrid>
            </VStack>
          </Box>

          {/* Pricing Section Header */}
          <VStack spacing={2} textAlign="center" pt={4}>
            <HelivanText variant="sectionHeader" colorOverride={HelivanColor.text.helivanBlue}>
              Choose the right plan for you
            </HelivanText>
            <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
              Start for free and upgrade as your needs grow
            </HelivanText>
          </VStack>

          <Flex
            gap={4}
            flexWrap="wrap"
            justifyContent="center"
            alignItems="stretch"
          >
            {tiers.map((tier) => (
              <PricingCard key={tier.name} tier={tier} onContactClick={onOpen} />
            ))}
          </Flex>

          {/* À La Carte Section */}
          <Box
            bg={HelivanColor.background.primary}
            borderRadius="12px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            p={6}
            width="full"
            maxWidth="800px"
          >
            <VStack spacing={5} align="stretch">
              <VStack spacing={1} textAlign="center">
                <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
                  À La Carte Add-ons
                </HelivanText>
                <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                  Customize your plan with additional resources
                </HelivanText>
              </VStack>

              <Box height="1px" bg={HelivanColor.border.primary} />

              <Flex gap={6} flexWrap="wrap" justifyContent="center">
                {/* Additional Projects */}
                <Box
                  bg={HelivanColor.background.lightestGrey}
                  borderRadius="8px"
                  p={4}
                  width="280px"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                >
                  <HStack spacing={4} align="start">
                    <Flex
                      bg={HelivanColor.text.blueLight}
                      p={2}
                      borderRadius="8px"
                      flexShrink={0}
                    >
                      <Icon as={FaRegFolder} boxSize={5} color={HelivanColor.text.helivanBlue} />
                    </Flex>
                    <VStack align="start" spacing={1} flex={1}>
                      <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.black}>
                        Additional 3 Projects
                      </HelivanText>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        Up to 50 agents per project
                      </HelivanText>
                      <HStack spacing={0} align="baseline" pt={1}>
                        <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
                          $9
                        </HelivanText>
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                          /mo
                        </HelivanText>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>

                {/* Additional Team Member */}
                <Box
                  bg={HelivanColor.background.lightestGrey}
                  borderRadius="8px"
                  p={4}
                  width="280px"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                >
                  <HStack spacing={4} align="start">
                    <Flex
                      bg={HelivanColor.text.blueLight}
                      p={2}
                      borderRadius="8px"
                      flexShrink={0}
                    >
                      <Icon as={GoPeople} boxSize={5} color={HelivanColor.text.helivanBlue} />
                    </Flex>
                    <VStack align="start" spacing={1} flex={1}>
                      <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.black}>
                        Additional Seat
                      </HelivanText>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        Team plan only (up to 15)
                      </HelivanText>
                      <HStack spacing={0} align="baseline" pt={1}>
                        <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
                          $29
                        </HelivanText>
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                          /mo per seat
                        </HelivanText>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>

                {/* More Frequent Updates */}
                <Box
                  bg={HelivanColor.background.lightestGrey}
                  borderRadius="8px"
                  p={4}
                  width="280px"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                >
                  <HStack spacing={4} align="start">
                    <Flex
                      bg={HelivanColor.text.blueLight}
                      p={2}
                      borderRadius="8px"
                      flexShrink={0}
                    >
                      <Icon as={IoFlash} boxSize={5} color={HelivanColor.text.helivanBlue} />
                    </Flex>
                    <VStack align="start" spacing={1} flex={1}>
                      <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.black}>
                        Faster Updates
                      </HelivanText>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        More frequent monitoring
                      </HelivanText>
                      <HStack spacing={0} align="baseline" pt={1}>
                        <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
                          $3
                        </HelivanText>
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                          /project/mo
                        </HelivanText>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
              </Flex>
            </VStack>
          </Box>

          <Box
            bg={HelivanColor.background.primary}
            borderRadius="12px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            p={6}
            width="full"
            maxWidth="800px"
          >
            <VStack spacing={3} textAlign="center">
              <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
                Need something custom?
              </HelivanText>
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                Contact our sales team to discuss enterprise solutions tailored to your organization's needs.
              </HelivanText>
              <HelivanButton variant="outline" size="sm" onClick={onOpen}>
                Contact Sales
              </HelivanButton>
            </VStack>
          </Box>
        </VStack>
      </Flex>
    </Stack>
  );
};
